import type { FC } from 'react';
import { lazy } from 'react';

import { Outlet, useNavigate } from 'react-router';

import { DashboardEditLockProvider } from '@/features/Dashboard/context/DashboardEditLockProvider';
import { ExportProvider } from '@/features/DataHub/modules/Export/context/ExportContext';
import SymbolSelector from '@/features/SymbolSelector/components';
import { useAppSelector } from '@/hooks/redux-hooks';
import { useGxTokenFromStorage, useTripTokenFromStorage } from '@/services/storage';
import { withSuspense } from '@/utils/withSuspense';

const ErrorPage = lazy(() => import('@/pages/ErrorPage'));

/**
 * Public layout for unauthenticated routes.
 *
 * - Allows access to public pages without authentication
 * - Handles auth failure states by redirecting to logout or showing 404
 * - Used for landing pages, login, registration, etc.
 * - Provides ExportProvider and SymbolSelector for authenticated users
 */
export const PublicLayout: FC = () => {
  const { status } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const gxToken = useGxTokenFromStorage();
  const tripToken = useTripTokenFromStorage();

  if (status === 'failed') {
    if (!!gxToken && !!tripToken) {
      navigate('/logout');
      return null;
    }
    return withSuspense(<ErrorPage />);
  }

  if (gxToken) {
    return (
      <DashboardEditLockProvider>
        <ExportProvider>
          <SymbolSelector />
          <Outlet />
        </ExportProvider>
      </DashboardEditLockProvider>
    );
  }

  return <Outlet />;
};
